<template>
  <div class="media-page page-banners h-full w-full p-4">
    <section class="media-hero mb-5">
      <div>
        <h2 class="text-xl font-bold mb-2">Contenido Visual del Reproductor</h2>
        <p class="hero-subtitle">Sube imagenes y videos cortos para mostrarlos cuando no haya video musical en vivo.</p>
      </div>
    </section>

    <section class="media-toolbar mb-4">
      <div class="toolbar-field">
        <label class="field-label block mb-1">Canal</label>
        <Dropdown v-model="selectedRadio" :options="radios" optionLabel="name" optionValue="id"
          placeholder="Selecciona un canal" class="channel-select w-full"
          filter filterPlaceholder="Buscar canal..." :showClear="false"
          @change="onRadioChange()">
          <template #value="slotProps">
            <div v-if="slotProps.value && findRadioById(slotProps.value)" class="channel-option-content">
              <div class="channel-logo-shell">
                <img
                  v-if="getRadioLogo(findRadioById(slotProps.value))"
                  :src="getRadioLogo(findRadioById(slotProps.value))"
                  :alt="findRadioById(slotProps.value).name"
                  class="channel-logo"
                  @error="onRadioLogoError(findRadioById(slotProps.value))"
                />
                <span v-else class="channel-logo-fallback">{{ getRadioInitials(findRadioById(slotProps.value).name) }}</span>
              </div>
              <span class="channel-option-label">{{ findRadioById(slotProps.value).name }}</span>
            </div>
            <span v-else class="channel-placeholder">Selecciona un canal</span>
          </template>

          <template #option="slotProps">
            <div class="channel-option-content">
              <div class="channel-logo-shell">
                <img
                  v-if="getRadioLogo(slotProps.option)"
                  :src="getRadioLogo(slotProps.option)"
                  :alt="slotProps.option.name"
                  class="channel-logo"
                  @error="onRadioLogoError(slotProps.option)"
                />
                <span v-else class="channel-logo-fallback">{{ getRadioInitials(slotProps.option.name) }}</span>
              </div>
              <span class="channel-option-label">{{ slotProps.option.name }}</span>
            </div>
          </template>
        </Dropdown>
      </div>

      <div class="toolbar-actions">
        <Button label="Subir contenido" icon="pi pi-upload"
          class="icon-button rounded upload-action-btn" @click="openUploadDialog()" :disabled="!selectedRadio" />
      </div>
    </section>

    <!-- Configuracion del canal (animation + random) -->
    <div v-if="selectedRadio" class="media-config-card mb-4 p-3 flex gap-6 items-end flex-wrap">
      <div>
        <label class="field-label block mb-1">Animacion de transicion</label>
        <Dropdown v-model="config.animation" :options="animationOptions" optionLabel="label" optionValue="value"
          class="config-select w-48" @change="saveConfig()" />
      </div>
      <div class="flex items-center gap-2">
        <Checkbox v-model="config.random" :binary="true" inputId="randomChk" @change="saveConfig()" />
        <label for="randomChk" class="text-sm">Reproducir en orden aleatorio</label>
      </div>
      <small class="text-gray-500">Cambios se guardan automaticamente.</small>
    </div>

    <!-- Tabla de contenido -->
    <section v-if="selectedRadio" class="media-table-wrap">
      <DataTable :value="items" :loading="loading" responsiveLayout="scroll"
        class="p-datatable-sm media-table">
        <Column header="Preview" style="width: 100px">
          <template #body="slotProps">
            <img v-if="slotProps.data.type === 0" :src="cdnBase + 'visual_content/' + slotProps.data.file"
              class="w-16 h-12 object-cover rounded cursor-pointer" @click="previewItem(slotProps.data)" />
            <video v-else :src="cdnBase + 'visual_content/' + slotProps.data.file"
              class="w-16 h-12 object-cover rounded cursor-pointer" muted @click="previewItem(slotProps.data)" />
          </template>
        </Column>
        <Column field="title" header="Titulo">
          <template #body="slotProps">
            <span>{{ slotProps.data.title || '-' }}</span>
          </template>
        </Column>
        <Column header="Tipo" style="width: 90px">
          <template #body="slotProps">
            <Tag :value="slotProps.data.type === 0 ? 'Imagen' : 'Video'"
              :severity="slotProps.data.type === 0 ? 'info' : 'warning'" />
          </template>
        </Column>
        <Column header="Variante">
          <template #body="slotProps">
            <span>{{ variantLabel(slotProps.data.variant_id) }}</span>
          </template>
        </Column>
        <Column header="Rango fechas">
          <template #body="slotProps">
            <span class="text-xs">{{ formatRange(slotProps.data.from, slotProps.data.to) }}</span>
          </template>
        </Column>
        <Column header="Horario">
          <template #body="slotProps">
            <span class="text-xs">{{ formatHourRange(slotProps.data.hour_from, slotProps.data.hour_to) }}</span>
          </template>
        </Column>
        <Column field="duration" header="Dur(s)" style="width: 80px">
          <template #body="slotProps">
            <span>{{ slotProps.data.duration || 10 }}</span>
          </template>
        </Column>
        <Column header="Orden" style="width: 110px">
          <template #body="slotProps">
            <div class="flex gap-1">
              <Button icon="pi pi-arrow-up" severity="secondary" text size="small"
                @click="moveUp(slotProps.index)" :disabled="slotProps.index === 0" />
              <Button icon="pi pi-arrow-down" severity="secondary" text size="small"
                @click="moveDown(slotProps.index)" :disabled="slotProps.index === items.length - 1" />
            </div>
          </template>
        </Column>
        <Column header="Activo" style="width: 70px">
          <template #body="slotProps">
            <Checkbox v-model="slotProps.data.active" :binary="true"
              @change="toggleActive(slotProps.data)" />
          </template>
        </Column>
        <Column header="Acciones" style="width: 110px">
          <template #body="slotProps">
            <div class="flex gap-1">
              <Button icon="pi pi-pencil" severity="info" text size="small"
                @click="openEditDialog(slotProps.data)" />
              <Button icon="pi pi-trash" severity="danger" text size="small"
                @click="confirmDelete(slotProps.data)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </section>

    <section v-else class="media-empty-state">
      <i class="pi pi-images media-empty-icon"></i>
      <p class="media-empty-title">Selecciona un canal</p>
      <p class="media-empty-hint">Carga, organiza y activa contenido visual del reproductor desde un solo lugar.</p>
    </section>

    <!-- Dialog de upload -->
    <Dialog v-model:visible="uploadDialogVisible" modal header="Subir contenido visual"
      :style="{ width: '60vw' }" :breakpoints="{ '1199px': '80vw', '575px': '95vw' }">

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label class="field-label block mb-1">Tipo de contenido</label>
          <Dropdown v-model="form.type" :options="typeOptions" optionLabel="label" optionValue="value"
            placeholder="Selecciona tipo" class="w-full" />
        </div>

        <div>
          <label class="field-label block mb-1">Titulo</label>
          <InputText v-model="form.title" class="w-full" maxlength="191" placeholder="Opcional" />
        </div>

        <div class="md:col-span-2">
          <label class="field-label block mb-1">Archivo</label>
          <input type="file" ref="fileInput" @change="onFileSelect($event.target.files[0])"
            :accept="form.type === 0 ? 'image/jpeg,image/png,image/webp' : 'video/mp4'" class="w-full" />
        </div>

        <div>
          <label class="field-label block mb-1">Desde (fecha/hora)</label>
          <Calendar v-model="form.from" showTime hourFormat="24" showIcon
            dateFormat="yy-mm-dd" class="w-full" placeholder="Inicio de publicacion"
            @show="primePicker('form', 'from')" />
        </div>

        <div>
          <label class="field-label block mb-1">Hasta (fecha/hora)</label>
          <Calendar v-model="form.to" showTime hourFormat="24" showIcon
            dateFormat="yy-mm-dd" class="w-full" placeholder="Fin de publicacion"
            @show="primePicker('form', 'to')" />
        </div>

        <div>
          <label class="field-label block mb-1">Hora diaria desde</label>
          <Calendar v-model="form.hour_from" timeOnly hourFormat="24" class="w-full" placeholder="08:00"
            @show="primePicker('form', 'hour_from')" />
        </div>

        <div>
          <label class="field-label block mb-1">Hora diaria hasta</label>
          <Calendar v-model="form.hour_to" timeOnly hourFormat="24" class="w-full" placeholder="22:00"
            @show="primePicker('form', 'hour_to')" />
        </div>

        <div>
          <label class="field-label block mb-1">Variante (sucursal)</label>
          <Dropdown v-model="form.variant_id" :options="variantOptions" optionLabel="name" optionValue="id"
            placeholder="Todas" class="w-full" />
        </div>

        <div>
          <label class="field-label block mb-1">Duracion en pantalla (segundos)</label>
          <InputNumber v-model="form.duration" :min="1" :max="600" class="w-full" showButtons />
        </div>
      </div>

      <!-- Preview archivo -->
      <div v-if="previewUrl" class="mt-3">
        <img v-if="form.type === 0" :src="previewUrl" class="max-h-48 rounded" />
        <video v-else :src="previewUrl" controls class="max-h-48 rounded" />
      </div>

      <!-- Barra de progreso -->
      <div class="w-full bg-gray-200 rounded-full h-2.5 my-3" v-if="uploading">
        <div class="bg-violet-600 h-2.5 rounded-full" :style="{ width: uploadProgress + '%' }"></div>
      </div>

      <template #footer>
        <Button label="Subir" icon="pi pi-upload" severity="success" outlined
          class="icon-button rounded" @click="uploadFile()" :disabled="!selectedFile || uploading" />
        <Button label="Cerrar" icon="pi pi-times" severity="info" outlined
          class="icon-button rounded" @click="uploadDialogVisible = false" :disabled="uploading" />
      </template>
    </Dialog>

    <!-- Dialog de edicion -->
    <Dialog v-model:visible="editDialogVisible" modal header="Editar contenido visual"
      :style="{ width: '55vw' }" :breakpoints="{ '1199px': '80vw', '575px': '95vw' }">

      <div v-if="editForm" class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div class="md:col-span-2">
          <label class="field-label block mb-1">Titulo</label>
          <InputText v-model="editForm.title" class="w-full" maxlength="191" />
        </div>

        <div>
          <label class="field-label block mb-1">Desde (fecha/hora)</label>
          <Calendar v-model="editForm.from" showTime hourFormat="24" showIcon
            dateFormat="yy-mm-dd" class="w-full"
            @show="primePicker('editForm', 'from')" />
        </div>

        <div>
          <label class="field-label block mb-1">Hasta (fecha/hora)</label>
          <Calendar v-model="editForm.to" showTime hourFormat="24" showIcon
            dateFormat="yy-mm-dd" class="w-full"
            @show="primePicker('editForm', 'to')" />
        </div>

        <div>
          <label class="field-label block mb-1">Hora diaria desde</label>
          <Calendar v-model="editForm.hour_from" timeOnly hourFormat="24" class="w-full"
            @show="primePicker('editForm', 'hour_from')" />
        </div>

        <div>
          <label class="field-label block mb-1">Hora diaria hasta</label>
          <Calendar v-model="editForm.hour_to" timeOnly hourFormat="24" class="w-full"
            @show="primePicker('editForm', 'hour_to')" />
        </div>

        <div>
          <label class="field-label block mb-1">Variante (sucursal)</label>
          <Dropdown v-model="editForm.variant_id" :options="variantOptions" optionLabel="name" optionValue="id"
            placeholder="Todas" class="w-full" />
        </div>

        <div>
          <label class="field-label block mb-1">Duracion en pantalla (segundos)</label>
          <InputNumber v-model="editForm.duration" :min="1" :max="600" class="w-full" showButtons />
        </div>
      </div>

      <template #footer>
        <Button label="Guardar" icon="pi pi-check" severity="success" outlined
          class="icon-button rounded" @click="saveEdit()" :disabled="savingEdit" />
        <Button label="Cancelar" icon="pi pi-times" severity="info" outlined
          class="icon-button rounded" @click="editDialogVisible = false" :disabled="savingEdit" />
      </template>
    </Dialog>

    <!-- Dialog de preview -->
    <Dialog v-model:visible="previewDialogVisible" modal header="Vista previa"
      :style="{ width: '60vw' }" :breakpoints="{ '575px': '90vw' }">
      <div class="flex justify-center">
        <img v-if="previewData && previewData.type === 0"
          :src="cdnBase + 'visual_content/' + previewData.file" class="max-h-96 rounded" />
        <video v-else-if="previewData"
          :src="cdnBase + 'visual_content/' + previewData.file" controls autoplay class="max-h-96 rounded" />
      </div>
    </Dialog>

    <!-- Confirm dialog -->
    <ConfirmDialog />
    <Toast position="top-right" group="topr" />
  </div>
</template>

<script>
import axiosInstance from '../axiosInstance';

const TODAS_VARIANT = { id: 0, name: 'Todas las sucursales' };

export default {
  data() {
    return {
      cdnBase: 'https://media.vybroo.com/',
      radios: [],
      selectedRadio: null,
      items: [],
      loading: false,

      // Config del canal
      config: { animation: 'fade', random: false },
      animationOptions: [
        { label: 'Fade', value: 'fade' },
        { label: 'Slide', value: 'slide' },
        { label: 'Swipe', value: 'swipe' }
      ],

      // Variantes del canal (sucursales)
      variants: [],

      // Upload
      uploadDialogVisible: false,
      typeOptions: [
        { label: 'Imagen (JPG, PNG, WebP)', value: 0 },
        { label: 'Video corto (MP4)', value: 1 }
      ],
      form: this.emptyForm(),
      selectedFile: null,
      previewUrl: null,
      uploading: false,
      uploadProgress: 0,

      // Edicion
      editDialogVisible: false,
      editForm: null,
      savingEdit: false,

      // Preview
      previewDialogVisible: false,
      previewData: null,
    }
  },
  computed: {
    variantOptions() {
      return [TODAS_VARIANT, ...this.variants];
    }
  },
  mounted() {
    this.loadRadios();
  },
  methods: {
    findRadioById(radioId) {
      return this.radios.find((radio) => String(radio.id) === String(radioId)) || null;
    },

    normalizeLogoUrl(value) {
      if (!value || typeof value !== 'string') return null;
      if (value.startsWith('http://') || value.startsWith('https://')) return value;
      if (value.startsWith('//')) return 'https:' + value;
      if (value.startsWith('/')) return value;
      return null;
    },

    getRadioLogo(radio) {
      if (!radio || radio._logoBroken) return null;

      const logoKeys = ['logo', 'logo_url', 'logoUrl', 'image', 'image_url', 'thumbnail', 'thumb', 'icon', 'avatar'];
      for (const key of logoKeys) {
        const normalized = this.normalizeLogoUrl(radio[key]);
        if (normalized) return normalized;
      }

      return null;
    },

    getRadioInitials(name) {
      if (!name || typeof name !== 'string') return 'R';
      return name.trim().charAt(0).toUpperCase() || 'R';
    },

    onRadioLogoError(radio) {
      if (radio) {
        radio._logoBroken = true;
      }
    },

    emptyForm() {
      return {
        type: 0,
        title: '',
        from: null,
        to: null,
        hour_from: null,
        hour_to: null,
        variant_id: 0,
        duration: 10,
      };
    },

    // Al abrir un Calendar vacio, lo rellena con la hora actual y minutos = 00.
    // Si ya tiene valor (caso edicion) no lo toca.
    primePicker(obj, field) {
      const target = this[obj];
      if (!target || target[field]) return;
      const d = new Date();
      d.setMinutes(0, 0, 0);
      target[field] = d;
    },

    async loadRadios() {
      try {
        const res = await axiosInstance.post('/api/radios', {
          token: localStorage.getItem('token')
        });
        this.radios = res.data.items || [];
      } catch (e) {
        console.error('Error cargando radios:', e);
      }
    },

    async onRadioChange() {
      await Promise.all([
        this.loadContent(),
        this.loadVariants(),
        this.loadConfig(),
      ]);
    },

    async loadContent() {
      if (!this.selectedRadio) return;
      this.loading = true;
      try {
        const res = await axiosInstance.post('/api/visual_content_list', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio
        });
        this.items = res.data.items || [];
      } catch (e) {
        console.error('Error cargando contenido:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el contenido', life: 5000, group: 'topr' });
      } finally {
        this.loading = false;
      }
    },

    async loadVariants() {
      if (!this.selectedRadio) { this.variants = []; return; }
      try {
        const res = await axiosInstance.post('/api/variantsRadio', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio
        });
        // API@ok inyecta `success:true` al array numérico, lo que lo serializa como objeto
        const raw = res.data || {};
        const list = Array.isArray(raw) ? raw : Object.values(raw);
        this.variants = list.filter(v => v && typeof v === 'object' && v.id != null && v.name != null);
      } catch (e) {
        console.error('Error cargando variantes:', e);
        this.variants = [];
      }
    },

    async loadConfig() {
      if (!this.selectedRadio) return;
      try {
        const res = await axiosInstance.post('/api/visual_content_config_get', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio
        });
        const c = res.data.config || {};
        this.config.animation = c.animation || 'fade';
        this.config.random = !!c.random;
      } catch (e) {
        console.error('Error cargando config:', e);
      }
    },

    async saveConfig() {
      if (!this.selectedRadio) return;
      try {
        await axiosInstance.post('/api/visual_content_config_save', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio,
          animation: this.config.animation,
          random: this.config.random
        });
        this.$toast.add({ severity: 'success', summary: 'Guardado', detail: 'Configuracion actualizada', life: 2500, group: 'topr' });
      } catch (e) {
        console.error('Error guardando config:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar la configuracion', life: 5000, group: 'topr' });
      }
    },

    openUploadDialog() {
      this.form = this.emptyForm();
      this.selectedFile = null;
      this.previewUrl = null;
      this.uploadProgress = 0;
      this.uploading = false;
      this.uploadDialogVisible = true;
    },

    onFileSelect(file) {
      if (!file) return;

      // Validar tipo
      const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
      const isVideo = file.type === 'video/mp4';

      if (this.form.type === 0 && !isImage) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Selecciona una imagen JPG, PNG o WebP', life: 5000, group: 'topr' });
        return;
      }
      if (this.form.type === 1 && !isVideo) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Selecciona un video MP4', life: 5000, group: 'topr' });
        return;
      }

      const maxSize = this.form.type === 0 ? 10 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxLabel = this.form.type === 0 ? '10 MB' : '50 MB';
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'El archivo excede el limite de ' + maxLabel, life: 5000, group: 'topr' });
        return;
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);
    },

    toDbDateTime(d) {
      if (!d) return null;
      const p = n => String(n).padStart(2, '0');
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
        + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
    },

    toDbTime(d) {
      if (!d) return null;
      const p = n => String(n).padStart(2, '0');
      return p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
    },

    parseDateTime(s) {
      if (!s) return null;
      const d = new Date(s.replace(' ', 'T'));
      return isNaN(d.getTime()) ? null : d;
    },

    parseTime(s) {
      if (!s) return null;
      const [h, m, sec] = s.split(':');
      const d = new Date();
      d.setHours(+h || 0, +m || 0, +sec || 0, 0);
      return d;
    },

    async uploadFile() {
      if (!this.selectedFile || !this.selectedRadio) return;
      this.uploading = true;
      this.uploadProgress = 0;

      const ext = this.selectedFile.name.split('.').pop().toLowerCase();
      const timestamp = Date.now();
      const fileName = 'r' + this.selectedRadio + '_' + timestamp + '.' + ext;

      try {
        const presignedRes = await axiosInstance.post('/transmito/get_presigned_url', {
          fileName: 'visual_content/' + fileName
        });

        if (!presignedRes.data) throw new Error('No se obtuvo presigned URL');

        const contentType = this.form.type === 0 ? this.selectedFile.type : 'video/mp4';
        const uploadUrl = presignedRes.data.replace(
          'https://vybroo-media.36849d2e964ee51fea8d00d8d1f680eb.r2.cloudflarestorage.com/',
          'vybroo-media/'
        );

        await axiosInstance({
          url: uploadUrl,
          method: 'PUT',
          data: this.selectedFile,
          headers: { 'Content-Type': contentType },
          onUploadProgress: (e) => {
            this.uploadProgress = Math.round((e.loaded / e.total) * 100);
          }
        });

        await axiosInstance.post('/api/visual_content_save', {
          token: localStorage.getItem('token'),
          radio_id: this.selectedRadio,
          type: this.form.type,
          file: fileName,
          title: this.form.title || '',
          from: this.toDbDateTime(this.form.from),
          to: this.toDbDateTime(this.form.to),
          hour_from: this.toDbTime(this.form.hour_from),
          hour_to: this.toDbTime(this.form.hour_to),
          variant_id: this.form.variant_id || 0,
          duration: this.form.duration || 10,
        });

        this.$toast.add({ severity: 'success', summary: 'Listo', detail: 'Contenido subido correctamente', life: 5000, group: 'topr' });
        this.uploadDialogVisible = false;
        this.loadContent();

      } catch (e) {
        console.error('Error subiendo archivo:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Error al subir: ' + (e.message || e), life: 5000, group: 'topr' });
      } finally {
        this.uploading = false;
      }
    },

    openEditDialog(item) {
      this.editForm = {
        id: item.id,
        title: item.title || '',
        from: this.parseDateTime(item.from),
        to: this.parseDateTime(item.to),
        hour_from: this.parseTime(item.hour_from),
        hour_to: this.parseTime(item.hour_to),
        variant_id: item.variant_id || 0,
        duration: item.duration || 10,
      };
      this.editDialogVisible = true;
    },

    async saveEdit() {
      if (!this.editForm) return;
      this.savingEdit = true;
      try {
        await axiosInstance.post('/api/visual_content_update', {
          token: localStorage.getItem('token'),
          id: this.editForm.id,
          title: this.editForm.title || '',
          from: this.toDbDateTime(this.editForm.from),
          to: this.toDbDateTime(this.editForm.to),
          hour_from: this.toDbTime(this.editForm.hour_from),
          hour_to: this.toDbTime(this.editForm.hour_to),
          variant_id: this.editForm.variant_id || 0,
          duration: this.editForm.duration || 10,
        });
        this.$toast.add({ severity: 'success', summary: 'Guardado', detail: 'Contenido actualizado', life: 3000, group: 'topr' });
        this.editDialogVisible = false;
        this.loadContent();
      } catch (e) {
        console.error('Error editando:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar', life: 5000, group: 'topr' });
      } finally {
        this.savingEdit = false;
      }
    },

    async moveUp(index) {
      if (index === 0) return;
      const temp = this.items[index];
      this.items[index] = this.items[index - 1];
      this.items[index - 1] = temp;
      await this.saveOrder();
    },

    async moveDown(index) {
      if (index >= this.items.length - 1) return;
      const temp = this.items[index];
      this.items[index] = this.items[index + 1];
      this.items[index + 1] = temp;
      await this.saveOrder();
    },

    async saveOrder() {
      const reordered = this.items.map((item, i) => ({ id: item.id, sort_order: i + 1 }));
      try {
        await axiosInstance.post('/api/visual_content_reorder', {
          token: localStorage.getItem('token'),
          items: reordered
        });
      } catch (e) {
        console.error('Error reordenando:', e);
      }
    },

    async toggleActive(item) {
      try {
        await axiosInstance.post('/api/visual_content_toggle', {
          token: localStorage.getItem('token'),
          id: item.id
        });
      } catch (e) {
        console.error('Error toggling:', e);
        item.active = !item.active;
      }
    },

    confirmDelete(item) {
      this.$confirm.require({
        message: 'Eliminar este contenido?',
        header: 'Confirmar',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.deleteItem(item)
      });
    },

    async deleteItem(item) {
      try {
        await axiosInstance.post('/api/visual_content_delete', {
          token: localStorage.getItem('token'),
          id: item.id
        });
        this.$toast.add({ severity: 'success', summary: 'Eliminado', detail: 'Contenido eliminado', life: 3000, group: 'topr' });
        this.loadContent();
      } catch (e) {
        console.error('Error eliminando:', e);
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar', life: 5000, group: 'topr' });
      }
    },

    previewItem(item) {
      this.previewData = item;
      this.previewDialogVisible = true;
    },

    variantLabel(id) {
      if (!id) return 'Todas';
      const v = this.variants.find(x => x.id === id);
      return v ? v.name : '#' + id;
    },

    formatRange(from, to) {
      if (!from && !to) return 'Siempre';
      const f = from ? from.substring(0, 16) : '...';
      const t = to ? to.substring(0, 16) : '...';
      return f + ' → ' + t;
    },

    formatHourRange(from, to) {
      if (!from && !to) return 'Todo el dia';
      const f = from ? from.substring(0, 5) : '...';
      const t = to ? to.substring(0, 5) : '...';
      return f + ' → ' + t;
    }
  }
}
</script>

<style scoped>
@import "@/assets/media-module-base.css";

.page-banners {
  --media-hero-bg: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  --media-hero-border: rgba(14, 116, 144, 0.2);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(30, 41, 59, 0.88) 0%, rgba(15, 23, 42, 0.96) 100%);
  --media-hero-border-dark: rgba(56, 189, 248, 0.35);

  --media-control-border: rgba(14, 116, 144, 0.3);
  --media-control-border-hover: rgba(2, 132, 199, 0.62);
  --media-control-focus: rgba(56, 189, 248, 0.2);
  --media-control-border-dark: rgba(56, 189, 248, 0.35);

  --media-cta-bg: #0284c7;
  --media-cta-bg-hover: #0369a1;

  --media-table-border: rgba(148, 163, 184, 0.45);
  --media-table-bg: rgba(255, 255, 255, 0.92);
}

.media-config-card {
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: rgba(255, 255, 255, 0.9);
}

.dark .media-config-card {
  border-color: rgba(82, 82, 91, 0.4);
  background: rgba(24, 24, 27, 0.6);
}
</style>
